/** 비교 헤더 상단 툴바 — 뷰 스위치 + 편집 토글 + 편집 액션 + 보조 컨트롤(도움말/피드백/다운로드) */

import {
  CircleHelp,
  Download,
  Loader2,
  Pencil,
  RotateCcw,
  Save,
} from "lucide-react";
import type { ReactNode } from "react";
import { getSaveButtonState } from "@/hooks/ixd-edit-session";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { CompareHelpPopover } from "./compare-help-popover";
import {
  DsdRegionReviewToggle,
  type MemberAxisOption,
} from "./region-review-toggle";

interface CompareHeaderToolbarProps {
  /** 편집 가능 capability (ixdReportId 존재). false면 토글/액션 모두 비노출 또는 disabled */
  canEdit: boolean;
  /** canEdit 이 false 인 이유. 버튼 tooltip 으로 그대로 나간다. */
  editDisabledReason: string;
  isEditMode: boolean;
  onEditModeChange: (value: boolean) => void;
  hasUnsavedChanges: boolean;
  /** 현재 보이는 IXD chunk(=표)에 속한 미저장 변경 개수 */
  unsavedCountActiveChunk: number;
  /** 양 fsType 합산 미저장 변경 개수 */
  unsavedCountTotal: number;
  showRawValueInEdit: boolean;
  onToggleRawValue: () => void;
  onResetAllChanges: () => void;
  onSave: () => void;
  onDownload: () => void;
  /** 저장·초기화·다운로드 잠금 조건. 훅(useIxdEditSession)이 계산한 값을 그대로 받는다. */
  isActionLocked: boolean;
  /** 아래 둘은 각 버튼의 스피너 표시용 — 잠금 판정에는 쓰지 않는다. */
  saveInFlight: boolean;
  downloadInFlight: boolean;
  /** 배정 검토 모드의 member 축 선택 — 셀에 실을 축은 한 번에 하나다. */
  memberAxes?: MemberAxisOption[];
  reviewAxisId?: string | null;
  onReviewAxisChange?: (axisId: string | null) => void;
  /** 저장은 끝났고 서버 preview·대사 재조회가 백그라운드로 도는 중 */
  isReflecting: boolean;
  /** 우측 보조 컨트롤 — 도움말 클릭 (미지정 시 핸들러 없이 렌더) */
  onHelpClick?: () => void;
  /** 우측 보조 컨트롤 — 피드백 클릭 (미지정 시 핸들러 없이 렌더) */
  onFeedbackClick?: () => void;
  /** 비교 결과 전체를 self-contained HTML 로 다운로드. 미지정 시 버튼 비노출. */
  onDownloadHtml?: () => void;
  /** HTML 다운로드 준비(전체 chunk fetch+렌더) 진행 중 */
  isDownloadingHtml?: boolean;
  /** 검토 도구 자리 — 배정 표시 토글 앞에 놓인다(값 위치 검토 켜기 버튼) */
  reviewToolSlot?: ReactNode;
}

export const CompareHeaderToolbar = ({
  canEdit,
  editDisabledReason,
  isEditMode,
  onEditModeChange,
  hasUnsavedChanges,
  unsavedCountActiveChunk,
  unsavedCountTotal,
  showRawValueInEdit,
  onToggleRawValue,
  onResetAllChanges,
  onSave,
  onDownload,
  isActionLocked,
  saveInFlight,
  downloadInFlight,
  isReflecting,
  onHelpClick,
  onFeedbackClick,
  onDownloadHtml,
  isDownloadingHtml = false,
  memberAxes,
  reviewAxisId,
  onReviewAxisChange,
  reviewToolSlot,
}: CompareHeaderToolbarProps) => {
  // 잠금 조건은 훅이 소유한다 — 여기서 재계산하면 조건이 하나 늘 때 화면마다 갈라진다.
  const isBusy = isActionLocked;
  const saveButton = getSaveButtonState(saveInFlight, isReflecting);
  // 편집 OFF여도 미저장 변경은 저장/초기화할 수 있어야 한다.
  // 다운로드는 정책 확정 전까지 편집 모드에서만 노출한다.
  const showEditActions = canEdit && (isEditMode || hasUnsavedChanges);

  return (
    <div className="flex h-14 px-5 items-center gap-2 self-stretch border-b border-line-normal">
      <Button
        className="bg-primary-base text-static-white border-primary-base"
        disabled={false}
        size={28}
      >
        IXD-DSD 비교
      </Button>
      <Button
        className="border-line-normal text-label-disable"
        disabled={true}
        size={28}
      >
        IXD 비교
      </Button>

      <div className="flex items-center gap-2">
        <Button
          variant={isEditMode ? "solid" : "outlined"}
          color="secondary"
          size={28}
          onClick={() => onEditModeChange(!isEditMode)}
          disabled={!canEdit}
          aria-pressed={isEditMode}
          leftIcon={<Pencil />}
          title={
            !canEdit
              ? editDisabledReason
              : isEditMode
                ? "편집 종료"
                : "편집 시작"
          }
          // min-w + justify-between로 너비/아이콘 위치/텍스트 끝 위치를 ON/OFF 무관 고정
          className={cn(
            "min-w-[4rem] justify-between",
            !isEditMode && "bg-white",
          )}
        >
          {isEditMode ? "편집 중" : "편집"}
        </Button>
        {hasUnsavedChanges && (
          <span className="caption-2-medium text-label-alternative">
            수정 {unsavedCountActiveChunk}개 / 전체 수정 {unsavedCountTotal}개
          </span>
        )}
      </div>

      {/* 우측 클러스터 — 편집 액션 + 보조 컨트롤(도움말/피드백/다운로드).
          단일 ml-auto 로 항상 우측 고정 위치를 보장한다. */}
      <div className="ml-auto flex items-center gap-2">
        {reviewToolSlot}
        <DsdRegionReviewToggle
          memberAxes={memberAxes}
          reviewAxisId={reviewAxisId}
          onReviewAxisChange={onReviewAxisChange}
        />
        {showEditActions && (
          <div className="flex items-center gap-2">
            {isEditMode && (
              <Button
                variant={showRawValueInEdit ? "solid" : "outlined"}
                color="secondary"
                size="small"
                onClick={onToggleRawValue}
              >
                {showRawValueInEdit ? "원본값(원)" : "단위적용값"}
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={onResetAllChanges}
              disabled={!hasUnsavedChanges || isBusy}
              leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
            >
              초기화
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={onSave}
              disabled={!hasUnsavedChanges || isBusy}
              leftIcon={
                saveButton.inProgress ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )
              }
            >
              {saveButton.label}
            </Button>
            {isEditMode && (
              <Button
                variant="solid"
                color="primary"
                size="small"
                onClick={onDownload}
                disabled={isBusy}
                leftIcon={
                  downloadInFlight ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )
                }
              >
                IXD 파일 다운로드
              </Button>
            )}
          </div>
        )}

        {onDownloadHtml && !isEditMode && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            leftIcon={
              isDownloadingHtml ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <Download className="w-3.5 h-3.5 mr-1" />
              )
            }
            onClick={onDownloadHtml}
            disabled={isDownloadingHtml}
            title="비교 결과 전체를 HTML 파일로 저장합니다"
          >
            {isDownloadingHtml ? "준비 중..." : "HTML 다운로드"}
          </Button>
        )}

        <CompareHelpPopover>
          <IconButton
            variant="transparent"
            icon={<CircleHelp />}
            aria-label="도움말"
            onClick={onHelpClick}
          />
        </CompareHelpPopover>
        {/* <IconButton
          icon={<MessageSquare />}
          aria-label="댓글"
          showBadge
          onClick={onFeedbackClick}
        /> */}
      </div>
    </div>
  );
};
